export const ORG_KINDS = ["sector", "general_department", "department", "section"];
export const NODE_LABELS = { sector: "القطاع", general_department: "الإدارة العامة", department: "الإدارة", section: "القسم", track: "المسار الوظيفي", level: "المستوى الوظيفي", employee: "الموظف" };
export const EMPTY_MAP_FILTERS = { sector: [], general_department: [], department: [], title: [], employee: [], course: [], professional: [], academic: [] };

export function mapIndexes(data) {
  const units = new Map((data.units || []).map(u => [u.id, u]));
  const ancestry = id => {
    const path = [], seen = new Set();
    while (id && units.has(id) && !seen.has(id)) { seen.add(id); path.unshift(units.get(id)); id = units.get(id).parent_id; }
    return path;
  };
  const profileRoute = profile => {
    const route = new Map();
    for (const id of [...(profile.unit_path || []), profile.unit_id, ...ORG_KINDS.map(k => profile[`${k}_id`])]) {
      for (const unit of ancestry(id)) route.set(unit.kind, unit);
    }
    return route;
  };
  return { units, ancestry, profileRoute, tracks: new Map((data.tracks || []).map(t => [t.id, t])),
    profiles: new Map((data.profiles || []).map(p => [p.id, p])), resources: new Map((data.resources || []).map(r => [r.key, r])) };
}

export function mapFilterOptions(data) {
  const index = mapIndexes(data);
  const units = kind => (data.units || []).filter(u => u.kind === kind).map(u => ({ id: u.id, name: u.name,
    context: index.ancestry(u.id).slice(0, -1).map(p => p.name).join(" / ") }));
  return { sector: units("sector"), general_department: units("general_department"), department: units("department"),
    title: [...new Set((data.profiles || []).map(p => p.title).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ar")).map(name => ({ id: name, name })),
    employee: (data.employees || []).map(e => ({ id: e.id, name: e.name, context: e.job_title })),
    ...Object.fromEntries(["course", "professional", "academic"].map(kind => [kind, (data.resources || [])
      .filter(r => kind === "course" ? r.kind === "program" : r.category === kind)
      .map(r => ({ id: r.key, name: r.name, context: r.issuer || "" }))])) };
}

export function buildHaramainTree(data, filters = EMPTY_MAP_FILTERS) {
  const index = mapIndexes(data), root = { id: "root", children: [] }, nodes = new Map();
  const add = (parent, id, kind, name, extra = {}) => {
    let node = nodes.get(id);
    if (!node) { node = { id, kind, name, children: [], ...extra }; nodes.set(id, node); parent.children.push(node); }
    return node;
  };
  const ensureOrg = (route, last) => {
    let parent = root;
    for (let i = 0; i <= last; i++) {
      const kind = ORG_KINDS[i], unit = route.get(kind);
      // A section is optional in the source hierarchy; never invent one.
      if (kind === "section" && !unit) continue;
      parent = add(parent, unit ? `unit:${unit.id}` : `${parent.id}/missing:${kind}`, kind,
        unit?.name || `${NODE_LABELS[kind]} غير محدد`, { unitId: unit?.id, missing: !unit, contextOnly: unit?.context_only });
    }
    return parent;
  };
  // Include empty organizational branches and job profiles on initial entry.
  for (const unit of data.units || []) {
    const route = new Map(index.ancestry(unit.id).map(u => [u.kind, u]));
    ensureOrg(route, ORG_KINDS.indexOf(unit.kind));
  }
  const profileNodes = new Map();
  for (const profile of data.profiles || []) {
    const parent = ensureOrg(index.profileRoute(profile), 3), track = index.tracks.get(profile.track_id);
    const trackNode = add(parent, `${parent.id}/track:${track?.id || "unrecorded"}`, "track",
      track?.name || "المسار الوظيفي غير محدد", { missing: !track, trackId: track?.id, branchUnitId: parent.unitId });
    profileNodes.set(profile.id, add(trackNode, `profile:${profile.id}`, "level", profile.title || "المستوى الوظيفي غير محدد", { profileId: profile.id }));
  }
  for (const employee of data.employees || []) {
    let parent = profileNodes.get(employee.profile_id);
    if (!parent) parent = add(root, "unassigned", "unassigned", "موظفون دون ملف وظيفي مكتمل");
    add(parent, `employee:${employee.id}`, "employee", employee.name, { employee, profileId: employee.profile_id });
  }
  const resourceKeys = ["course", "professional", "academic"].flatMap(k => filters[k] || []);
  const resourceFilters = ["course", "professional", "academic"].filter(k => filters[k]?.length);
  const requireEmployee = !!filters.employee?.length || resourceFilters.length > 0;
  const personFilter = requireEmployee || !!filters.title?.length;
  const orgMatch = profile => ORG_KINDS.slice(0, 3).every(kind => !filters[kind]?.length || filters[kind].includes(index.profileRoute(profile).get(kind)?.id));
  const profileMatch = profile => !!profile && orgMatch(profile) && (!filters.title?.length || filters.title.includes(profile.title));
  const emptyUnitMatch = node => !personFilter && node.unitId && ORG_KINDS.slice(0, 3).every(kind => {
    if (!filters[kind]?.length) return true;
    const path = index.ancestry(node.unitId).map(u => u.id);
    return filters[kind].some(id => path.includes(id) || index.ancestry(id).some(u => u.id === node.unitId));
  });
  let employeeCount = 0, itemCount = 0;
  const prune = node => {
    if (node.kind === "employee") {
      const employee = node.employee, profile = index.profiles.get(employee.profile_id);
      const anyOrg = ORG_KINDS.slice(0, 3).some(k => filters[k]?.length);
      if (profile ? !profileMatch(profile) : anyOrg || filters.title?.length) return null;
      if (filters.employee?.length && !filters.employee.includes(employee.id)) return null;
      if (!resourceFilters.every(kind => employee.items.some(item => filters[kind].includes(item.key)))) return null;
      const items = employee.items.filter(item => index.resources.has(item.key) && (resourceKeys.length ? resourceKeys.includes(item.key) : item.assigned))
        .map(item => ({ ...item, resource: index.resources.get(item.key) }));
      employeeCount++; itemCount += items.length;
      return { ...node, items, employeeCount: 1 };
    }
    const children = node.children.map(prune).filter(Boolean);
    const keepLevel = node.kind === "level" && !requireEmployee && profileMatch(index.profiles.get(node.profileId));
    if (!children.length && !keepLevel && !emptyUnitMatch(node)) return null;
    return { ...node, children, employeeCount: children.reduce((sum, child) => sum + (child.employeeCount || 0), 0) };
  };
  const roots = root.children.map(prune).filter(Boolean);
  const branchIds = [];
  const visit = list => { for (const node of list) { if (node.children.length || node.kind === "employee") branchIds.push(node.id); visit(node.children); } };
  visit(roots);
  return { roots, employeeCount, itemCount, branchIds };
}
